/**
 * Agency marketing layer — wraps the template demo in a sales site.
 *
 * Renders:
 *   - hero pitch (above the template demo)
 *   - demo intro callout (sits between hero and templates)
 *   - who-it's-for
 *   - process (3 steps)
 *   - why-us
 *   - contact / GHL booking embed
 *   - footer
 *
 * Templates still mount into #app between the hero and the bottom sections.
 * Edit agencyConfig below to retune copy / contact / embed URL.
 */
import { el } from './core/utils.js';
import { SHOWCASE } from './brands/registry.js';

export const agencyConfig = {
  brandName: 'POP3D',
  tagline: 'Vibrant 3D product sites. Live in days.',
  hero: {
    eyebrow: 'WEB · DESIGN · 3D',
    headline: 'Product sites that\nmake people\nstop scrolling.',
    lede: 'We build photoreal 3D product showcase sites. Custom Three.js, scroll-driven, Stripe-ready, AR-ready. Yours forever — no subscription.',
    primaryCTA: 'Book a 15-min call',
    secondaryCTA: '↓ See templates live',
  },
  whoFor: {
    title: 'Who it\'s for',
    body: 'We build for brands with 3-12 hero products who want their site to feel as good as the thing they\'re selling.',
    tags: ['DTC drops', 'Watches', 'Spirits', 'Cosmetics', 'Single-product launches', 'Limited editions', 'Premium electronics', 'Furniture', 'Wellness'],
  },
  process: {
    title: 'How it works',
    steps: [
      { n: '01', title: 'Intake', body: 'Send your brand assets + product list. We pick the template that fits.' },
      { n: '02', title: 'Build', body: 'We configure colors, materials, framing, GLB models. AR + Stripe wired in.' },
      { n: '03', title: 'Launch', body: 'Deploy to your domain. You own everything. We move on.' },
    ],
  },
  why: {
    title: 'Why us',
    points: [
      'Real Three.js + GSAP — not a Webflow plugin.',
      'Your own .glb models (we can source or create them).',
      'Stripe Payment Links, Shopify, or your checkout of choice.',
      'AR-ready out of the box — iOS Quick Look + Android Scene Viewer.',
      'Yours forever. No subscription, no lock-in.',
    ],
  },
  contact: {
    title: 'Get a quote',
    body: 'Tell us about your product. We send a quote within 24 hours.',
    // Drop your GHL calendar embed URL here. Example:
    //   https://api.leadconnectorhq.com/widget/booking/<your-calendar-id>
    ghlEmbed: '',
    fallbackEmail: 'brandon@profitpivot.ca',
  },
};

export function mountAgency(rootBefore, rootAfter) {
  const c = agencyConfig;

  // ---- HERO ----
  const headlineEl = el('h1', {});
  c.hero.headline.split('\n').forEach((line, i, arr) => {
    headlineEl.appendChild(document.createTextNode(line));
    if (i < arr.length - 1) headlineEl.appendChild(document.createElement('br'));
  });

  const hero = el('section', { className: 'agency-hero' },
    el('header', { className: 'agency-nav' },
      el('a', { className: 'agency-logo', href: '#' }, c.brandName),
      el('nav', {},
        ...SHOWCASE.map((s) => el('a', { href: '/' + s.slug, className: 'agency-nav__store' }, s.label)),
        el('a', { href: '#contact' }, 'Contact'),
      ),
    ),
    el('div', { className: 'agency-hero__copy' },
      el('p', { className: 'agency-eyebrow' }, c.hero.eyebrow),
      headlineEl,
      el('p', { className: 'agency-lede' }, c.hero.lede),
      el('div', { className: 'agency-cta-row' },
        el('a', { className: 'agency-cta', href: '#demo' }, 'Visit a live store ↓'),
        el('a', { className: 'agency-cta agency-cta--ghost', href: '#contact' }, c.hero.primaryCTA),
      ),
    ),
    // Quick store buttons right under the hero — above the fold, unmissable.
    el('div', { className: 'agency-hero__stores' },
      ...SHOWCASE.map((s) =>
        el('a', {
          className: 'agency-hero__store-btn',
          href: '/' + s.slug,
          style: { '--swatch': s.tint },
        },
          el('span', { className: 'agency-hero__store-dot', style: { background: s.tint } }),
          el('span', {}, s.label),
          el('span', { className: 'agency-hero__store-vert' }, s.vertical),
        ),
      ),
    ),
  );

  // Live storefront links — each opens a real pop-up store in its vertical.
  const showcaseRow = el('div', { className: 'agency-showcase' },
    ...SHOWCASE.map((s) =>
      el('a', {
        className: 'agency-showcase__card',
        href: '/' + s.slug,
        style: { '--swatch': s.tint },
      },
        el('span', { className: 'agency-showcase__dot', style: { background: s.tint } }),
        el('span', { className: 'agency-showcase__label' }, s.label),
        el('span', { className: 'agency-showcase__vertical' }, s.vertical),
        el('span', { className: 'agency-showcase__go' }, 'Open store →'),
      ),
    ),
  );

  const demoIntro = el('section', { className: 'agency-demo-intro', id: 'demo' },
    el('p', { className: 'agency-eyebrow' }, 'Live pop-up stores'),
    el('h2', {}, 'Three verticals. Three live stores.'),
    el('p', { className: 'agency-lede' }, 'Each is a real, working storefront — beauty, supplements, activewear. Open one, scroll the 3D, switch templates. This is what your drop looks like.'),
    showcaseRow,
    el('p', { className: 'agency-demo-intro__or' }, 'Or preview the template engine itself below ↓'),
  );

  rootBefore.appendChild(hero);
  rootBefore.appendChild(demoIntro);

  // ---- POST-DEMO SECTIONS ----
  const whoFor = el('section', { className: 'agency-section agency-whofor' },
    el('p', { className: 'agency-eyebrow' }, c.whoFor.title),
    el('h2', {}, c.whoFor.body),
    el('ul', { className: 'agency-tags' },
      ...c.whoFor.tags.map((t) => el('li', {}, t)),
    ),
  );

  const process = el('section', { className: 'agency-section agency-process', id: 'process' },
    el('p', { className: 'agency-eyebrow' }, c.process.title),
    el('ol', { className: 'agency-steps' },
      ...c.process.steps.map((s) =>
        el('li', {},
          el('span', { className: 'agency-step-n' }, s.n),
          el('h3', {}, s.title),
          el('p', {}, s.body),
        ),
      ),
    ),
  );

  const why = el('section', { className: 'agency-section agency-why' },
    el('p', { className: 'agency-eyebrow' }, c.why.title),
    el('ul', { className: 'agency-why-list' },
      ...c.why.points.map((p) => el('li', {}, p)),
    ),
  );

  // Contact: GHL embed iframe + fallback email.
  const contactBody = el('div', { className: 'agency-contact__body' });
  if (c.contact.ghlEmbed) {
    contactBody.appendChild(
      el('iframe', {
        className: 'agency-ghl-iframe',
        src: c.contact.ghlEmbed,
        title: 'Book a call',
        loading: 'lazy',
      }),
    );
  } else {
    contactBody.appendChild(
      el('div', { className: 'agency-ghl-placeholder' },
        el('p', {}, 'GHL booking embed not configured. Edit src/agency.js → agencyConfig.contact.ghlEmbed with your calendar URL.'),
        el('p', {}, 'Or email: ',
          el('a', { href: 'mailto:' + c.contact.fallbackEmail }, c.contact.fallbackEmail),
        ),
      ),
    );
  }

  const contact = el('section', { className: 'agency-section agency-contact', id: 'contact' },
    el('p', { className: 'agency-eyebrow' }, c.contact.title),
    el('h2', {}, c.contact.body),
    contactBody,
  );

  const footer = el('footer', { className: 'agency-footer' },
    el('div', {}, '© ' + new Date().getFullYear() + ' · ' + c.brandName),
    el('div', {}, c.tagline),
    el('a', { href: 'mailto:' + c.contact.fallbackEmail }, c.contact.fallbackEmail),
  );

  rootAfter.appendChild(whoFor);
  rootAfter.appendChild(process);
  rootAfter.appendChild(why);
  rootAfter.appendChild(contact);
  rootAfter.appendChild(footer);
}
